/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { useTheme } from 'emotion-theming';
import { FC, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Basket, Button, Form, Input, Page, Text, VoucherInfo } from '../..';
import { appStore } from '../../../state';
import { Copy, ROUTES, ThemeProps } from '../../../types';
import {
  isEmpty,
  isValidEmail,
  pricePresenter,
  textReplacer,
} from '../../../utils';
import { ErrorKeys } from '../../ui/Notification/Notification';
import { usePostUserSignin, useGetBillingStatus } from '../../../hooks';

interface LoginProps extends Copy { }

const Login: FC<LoginProps> = ({ fallbackCopy, localeCopy }) => {
  const location = useLocation();
  const { dispatch, state } = useContext(appStore);
  const theme: ThemeProps = useTheme();
  const history = useHistory();
  const { form, user, config, skipPayment, billingStatus } = state;
  const { currencySymbols } = config;

  // initialise form
  const { control, errors, handleSubmit } = useForm({
    defaultValues: {
      emailAddress: form.emailAddress || '',
      password: '',
    },
  });

  const billingStatusQuery = useGetBillingStatus({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
  });

  const {
    isLoading: postSigninIsLoading,
    mutateAsync: postUserSigninData,
  } = usePostUserSignin({
    mutationConfig: {
      onError: async (error: any) => {
        console.error('Error in user login', await error);
        const { code } = await error;
        if (code === ErrorKeys.INVALID_CREDENTIALS) {
          dispatch({
            type: 'ADD_MESSAGE',
            payload: {
              dismissable: true,
              id: new Date().getTime(),
              text:
                localeCopy?.errors?.INVALID_CREDENTIALS ||
                fallbackCopy.errors.INVALID_CREDENTIALS,
              timeout: 7,
              type: 'error',
            },
          });
          return;
        }
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            dismissable: true,
            id: new Date().getTime(),
            text:
              localeCopy?.errors?.GENERIC_ERROR ||
              fallbackCopy.errors.GENERIC_ERROR,
            timeout: 7,
            type: 'error',
          },
        });
      },
      onSuccess: (response: any) => {
        /**
        * Save the login response to state
        */
        /**
        * Response headers are stripped by default for security. We need to use a getter
        * to explicitly get the headers to set countryCode:
        * https://stackoverflow.com/a/44816592/9935125
        */
        const { data, resHeaders } = response;
        const countryCode = resHeaders.get('x-az-countrycode');
        const { apiKey, apiSecret, id, userType } = data;
        const cdnExpiry = data['cdn-expiry'];
        const cdnSessionId = data['cdn-sessionid'];
        dispatch({
          type: 'UPDATE_USER',
          payload: {
            apiKey,
            apiSecret,
            countryCode,
            emailAddress: form.emailAddress,
            id,
            cdnExpiry,
            cdnSessionId,
            userType,
          },
        });
        if (location.state) {
          /**
          * Navigate to the next location from state
          */
          if (!billingStatus?.billingStatus) {
            billingStatusQuery.refetch().then((response) => {
              history.replace((location as any).state.nextPath);
            });
          } else {
            history.replace((location as any).state.nextPath);
          }
        } else {
          /**
          * Navigate to the next page when
          */
          billingStatusQuery.refetch().then((response) => {
            const billingStatus = response.data;
            if (billingStatus?.billingStatus === 'SUBSCRIBED') {
              history.push(ROUTES.DASHBOARD);
            } else {
              history.push(ROUTES.PAYMENT);
            }
          }).catch((error) => {
            console.log(`Error processing billing status ${JSON.stringify(error)}`);
            history.push(ROUTES.DASHBOARD);
          });
        }
      },
    },
  });

  /**
  * Form submit success handler where we let react-hook-form validate, then we dispatch
  * our state action to save selected data in state and persist in localStorage.
  */
  const onSubmit = async (formData: any) => {
    const { emailAddress, password } = formData;
    /**
    * Save form data to state
    */
    /**
    * The API uses the key `userName` as the email address, but I want to keep around the
    * emailAddress data in state so we are adding both.
    */
    dispatch({
      type: 'UPDATE_FORM_DATA',
      payload: {
        emailAddress,
        userName: emailAddress,
      },
    });
    try {
      /**
      * Log user in
      */
      await postUserSigninData({
        password,
        userName: emailAddress,
      });
    } catch (error) {
      console.error('error: ', error);
    }
  };

  /**
  * Form submit error handler
  */
  const onError = (errors: any) => {
    console.error('Form errors: ', errors);
  };

  const { selectedPlan } = form;

  return (
    <Page
      isReady={true}
      pageNumber={2}
      title={localeCopy?.title || fallbackCopy?.title}>
      {!isEmpty(selectedPlan) && (
        skipPayment.skip ?
          (
            <VoucherInfo
              header={localeCopy?.components?.basket?.prepayVoucherAppliedHeader ||
                fallbackCopy.components.basket.prepayVoucherAppliedHeader}
              details={textReplacer(
                localeCopy?.components?.basket?.prepayVoucherAppliedDetails ||
                fallbackCopy.components.basket.prepayVoucherAppliedDetails,
                {
                  '{VOUCHER_DURATION}': selectedPlan?.discounts[0].durationInMonths,
                  '{MONTH}': selectedPlan!.discounts[0].durationInMonths === 1 ? (
                    localeCopy?.month?.singular || fallbackCopy.month.singular) :
                    (localeCopy?.month?.plural || fallbackCopy.month.plural)
                }
              )}
            />
          )
          :
          (
            <Basket
              {...form.selectedPlan}
              priceFrequency={
                localeCopy?.plans[selectedPlan!.billingCycle.toLowerCase()]
                  ?.priceFrequency ||
                fallbackCopy.plans[selectedPlan!.billingCycle.toLowerCase()]
                  .priceFrequency
              }
              title={
                localeCopy?.plans[selectedPlan!.billingCycle.toLowerCase()]
                  ?.title ||
                fallbackCopy.plans[selectedPlan!.billingCycle.toLowerCase()].title
              }
              currencySymbols={currencySymbols}
              accentMessage={(selectedPlan?.discounts && selectedPlan?.discounts?.length !== 0) ?
                ((selectedPlan?.discounts[0]?.percentage < 100) ?
                  (textReplacer(
                    localeCopy?.plans[selectedPlan!.billingCycle.toLowerCase()]?.accentSecondaryMessage ||
                    fallbackCopy.plans[selectedPlan!.billingCycle.toLowerCase()].accentSecondaryMessage,
                    {
                      '{VOUCHER_PERCENTAGE}': selectedPlan?.discounts[0].percentage,
                      '{VOUCHER_DURATION}': selectedPlan?.discounts[0].durationInMonths,
                      '{MONTH}': selectedPlan?.discounts[0].durationInMonths === 1 ? (
                        localeCopy?.month?.singular || fallbackCopy.month.singular) :
                        (localeCopy?.month?.plural || fallbackCopy.month.plural)
                    })
                  ) :
                  (
                    (textReplacer(
                      localeCopy?.plans[selectedPlan!.billingCycle.toLowerCase()]?.accentTertiaryMessage ||
                      fallbackCopy.plans[selectedPlan!.billingCycle.toLowerCase()].accentTertiaryMessage,
                      {
                        '{VOUCHER_PERCENTAGE}': selectedPlan?.discounts[0].percentage,
                        '{VOUCHER_DURATION}': selectedPlan?.discounts[0].durationInMonths,
                        '{MONTH}': selectedPlan?.discounts[0].durationInMonths === 1 ? (
                          localeCopy?.month?.singular || fallbackCopy.month.singular) :
                          (localeCopy?.month?.plural || fallbackCopy.month.plural)
                      })
                    )
                  )
                ) :
                (
                  localeCopy?.plans[selectedPlan!.billingCycle.toLowerCase()]?.accentMessage ||
                  fallbackCopy.plans[selectedPlan!.billingCycle.toLowerCase()].accentMessage
                )
              }
              detailsMessage={textReplacer(
                localeCopy?.plans[selectedPlan!.billingCycle.toLowerCase()]?.detailsMessage ||
                fallbackCopy.plans[selectedPlan!.billingCycle.toLowerCase()].detailsMessage,
                {
                  '{PRICE}': pricePresenter(selectedPlan!.totalPrice, currencySymbols, selectedPlan!.currency)
                }
              )
              }
              additionalInfo={
                localeCopy?.plans[selectedPlan!.billingCycle.toLowerCase()]?.additionalInfo ||
                fallbackCopy.plans[selectedPlan!.billingCycle.toLowerCase()].additionalInfo
              }
              details={(selectedPlan!.discounts?.length !== 0 && selectedPlan!.discounts[0]?.percentage < 100) ?
                localeCopy?.plans[selectedPlan!.billingCycle.toLowerCase()]?.details ||
                fallbackCopy.plans[selectedPlan!.billingCycle.toLowerCase()].details
                :
                undefined
              }
            />
          ))}
      <Link
        css={{
          alignSelf: 'flex-start',
          fontSize: 12,
          marginBottom: 20,
        }}
        to={ROUTES.USER_REGISTRATION}>
        <Text>
          {localeCopy?.registerInstead || fallbackCopy.registerInstead}
        </Text>
      </Link>
      <Form
        disableEnterKeypress={false}
        onSubmit={handleSubmit(onSubmit, onError)}>
        <Controller
          control={control}
          name='emailAddress'
          render={({ name, onChange, value }) => (
            <Input
              errorMessage={errors && errors?.emailAddress?.message}
              isError={!isEmpty(errors?.emailAddress)}
              label={
                localeCopy?.emailAddress?.label ||
                fallbackCopy.emailAddress.label
              }
              name={name}
              onChange={onChange}
              placeholder={
                localeCopy?.emailAddress?.placeholder ||
                fallbackCopy.emailAddress.placeholder
              }
              type='email'
              value={value}
            />
          )}
          rules={{
            required: {
              message:
                localeCopy?.emailAddress?.errorMessage?.required ||
                fallbackCopy.emailAddress.errorMessage.required,
              value: true,
            },
            validate: {
              isValid: (value: string) =>
                isValidEmail(value) ||
                localeCopy?.emailAddress?.errorMessage?.invalid ||
                fallbackCopy.emailAddress.errorMessage.invalid,
            },
          }}
        />
        <Controller
          control={control}
          name='password'
          render={({ name, onChange, value }) => (
            <Input
              errorMessage={errors && errors?.password?.message}
              isError={!isEmpty(errors?.password)}
              label={localeCopy?.password?.label || fallbackCopy.password.label}
              name={name}
              onChange={onChange}
              placeholder={
                localeCopy?.password?.placeholder ||
                fallbackCopy.password.placeholder
              }
              type='password'
              value={value}
            />
          )}
          rules={{
            required: {
              message:
                localeCopy?.password?.errorMessage?.required ||
                fallbackCopy.password.errorMessage.required,
              value: true,
            },
          }}
        />
        <Link
          css={{ alignSelf: 'flex-start', fontSize: 12 }}
          to={ROUTES.USER_FORGOTTEN_PASSWORD}>
          <Text>
            {localeCopy?.forgottenPassword || fallbackCopy.forgottenPassword}
          </Text>
        </Link>
        <Button
          css={{
            marginTop: 40,
            marginBottom: 14,
            [theme.mq.desktop]: {
              marginTop: 48,
            },
          }}
          isBusy={postSigninIsLoading}
          isBusyText={
            localeCopy?.components?.button?.loading ||
            fallbackCopy?.components.button.loading
          }
          type='submit'
          variant='primary'
          onKeyUp={(e: any) => {
            if (e.key === 'Enter') {
              handleSubmit(onSubmit, onError).apply(this);
            }
          }}>
          {localeCopy?.submitButton || fallbackCopy?.submitButton}
        </Button>
      </Form>
    </Page>
  );
};

export default Login;
